import { useState, useEffect, useContext } from "react";
import { tss } from "tss-react/mui";
import { Button, MenuItem, ListItemIcon, Input, InputAdornment, Menu } from "@mui/material";
import { Scrollbars } from "react-custom-scrollbars";
import RasterActions from "actions/RasterActions";
import { Section } from "components";
import { handleLayerEvent } from "actions/ViewerTools";
import RasterImage from "./RasterImage";
import { Search, FilterList, ArrowDropDown, Add } from "@mui/icons-material";
import { RasterIcon } from "assets/images";
import commonStyles, { iconColor } from "styles/common";
import {ViewContext} from "panels/LeftPanel";

const useStyles = tss.create(({theme}) => ({
    sectionContainer: {
        borderTop: "solid 1px #f0f0f0",
        lineHeight: 0,
    },
    sectionLabel: {
        margin: 0,
        marginLeft: -5,
        display: "flex",
        alignItems: "center",
        fontWeight: "bold",
        fontSize: 14,
        color: theme.palette.text.secondary,
        width: 280,
    },
    content: {
        marginTop: 20,
        padding: "2px 0px 8px 15px",
    },
    label: {
        color: theme.palette.text.secondary,
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    row: {
        height: 35,
        display: "flex",
        alignItems: "center",
        width: "100%",
        position: "relative",
    },
    select: {
        width: 270,
        height: 40,
        display: "inline-flex",
        alignItems: "center",
        textOverflow: "ellipsis",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    numberInput: {
        //color: "black",
        textAlign: "right",
        fontSize: 14,
        marginTop: 0,
        width: 60,
        paddingLeft: '8px'
    },
    middle: {
        display: "flex",
        alignItems: "center",
    },
    name: {
        marginTop: -5,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        maxWidth: 270,
    },
    applyButton: {
        marginBottom: 0,
        marginTop: 5,
        textAlign: "right",
    },
    menu: {
        "& ul": {
            minWidth: 290,
            maxWidth: 320,
        },
        " & .MuiPaper-root": {
            overflowY: "hidden",
        },
    },
    menuItem: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: "100%",
    },
    icon: {
        width: 24,
        height: 24,
        color: theme.palette.text.secondary,
    },
}));

type globalState = {
    showRaster: boolean;
};

let globalSettings: globalState[] = [];

type IRasterImagery = {
    currentProject: any;
    layer: any;
};

export default function RasterImagery(props: IRasterImagery) {
    const { classes: common } = commonStyles();
    const { classes } = useStyles();
    const { currentProject, layer } = props; 
    const { 
        hasFullAccess,
        hasProjectAccess,
        hasActiveSubscription,
        isDefaultProject,
        sweetAlerts
    } = useContext<any>(ViewContext);

    const initialState = { showRaster: false };
    if (!globalSettings[layer.ID]) {
        globalSettings[layer.ID] = initialState;
    }

    const [rasterImagery, setRasterImagery] = useState(globalSettings[layer.ID]["showRaster"] || false);
    //const [selectedImagery, setSelectedImagery] = useState(-1);
    const [images, setImages] = useState([]);
    const [filteredImages, setFilteredImages] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [needsApply, setNeedsApply] = useState(false);
    const [update, setUpdate] = useState(false);

    const rasterList = layer["raster_list"] ? layer["raster_list"].trim().split(" ") : [];

    useEffect(() => {
        const images: any = Object.values(currentProject).filter(
            (entity: any) =>
                ((entity.file_type === "IDXORTHO" || entity.file_type === "ORTHOSET") && entity.state === "COMPLETE") ||
                (entity.file_type === "VOXSURF" &&
                    entity.source_ortho &&
                    entity.source_ortho !== "0" &&
                    entity.state === "COMPLETE") ||
                (entity.file_type === "WMSORTHO" && entity.state === "COMPLETE")
        );
        setImages(images);
        setFilteredImages(images);
    }, [currentProject]);

    if (!layer) {
        return <div />;
    }

    const toggleRasterImagery = (evt?: any) => {
        if (!hasActiveSubscription) {
            sweetAlerts();
            return;
        }
        globalSettings[layer.ID]["showRaster"] = !rasterImagery;
        setRasterImagery(!rasterImagery);
        if (evt) {
            setAnchorEl(evt.currentTarget);
        }
    };

    const addRasterImage = (e: any) => {
        var rasterId = e.currentTarget.value;
        var raster: any = filteredImages[rasterId];
        if (raster) {
            RasterActions.addRaster(raster, undefined, (selectedRaster: any) => {
                //console.log('selectedRaster:', selectedRaster);
                const list = layer["raster_list"];
                if (list) {
                    layer["raster_list"] = list.trim() + " " + selectedRaster.ID;
                } else {
                    layer["raster_list"] = selectedRaster.ID + " ";
                }
                const ortho = layer["ortho_list"];
                if (ortho) {
                    layer["ortho_list"] = ortho.trim() + " " + raster.ID;
                } else {
                    layer["ortho_list"] = raster.ID + " ";
                }
                toggleRasterImagery();
                console.log("add RasterImagery:", selectedRaster.ID);
                handleLayerEvent("refreshView", null, layer);
                setAnchorEl(null);
            });
        }
    };

    const searchImagery = (e: any) => {
        const value = e.target.value;
        //console.log('search:', value);
        if (value !== "") {
            var filteredImages: any = Object.values(images).filter(
                (image: any) =>
                    image.name.toLowerCase().substr(0, e.target.value.length) === e.target.value.toLowerCase() ||
                    image.name.toLowerCase().includes(e.target.value)
            );
            setFilteredImages(filteredImages);
        } else {
            setFilteredImages(images);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const updateParent = () => {
        if (isDefaultProject || !hasFullAccess) {
            sweetAlerts();
            return;
        }
        setUpdate(!update);
    };

    const refreshRasters = () => {
        if (isDefaultProject || !hasFullAccess) {
            sweetAlerts();
            return;
        }
        handleLayerEvent("refreshView", null, layer);
        setUpdate(!update);
    };

    function applySettings() {
        if (isDefaultProject || !hasFullAccess) {
            sweetAlerts();
            return;
        }
        setNeedsApply(false);
    }

    return (
        <Section name="rasterImagery" label="Image Layers">
            <div className={common.sectionContent}>
                <table style={{ width: "100%" }} cellPadding={0} cellSpacing={0}>
                    <tbody>
                        <tr className={classes.row} style={{ marginBottom: 10 }}>
                            <td style={{ width: "100%" }}>
                                <Button
                                    aria-controls="menu-list-grow"
                                    aria-haspopup="true"
                                    onClick={toggleRasterImagery}
                                    color="primary"
                                    disabled={!hasProjectAccess}
                                >
                                    <Add />
                                    <span
                                        style={{
                                            paddingLeft: 8,
                                            paddingRight: 8,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Add Image Layer
                                    </span>
                                    <ArrowDropDown fontSize="small" />
                                </Button>
                                <Menu
                                    id="menu-list-grow"
                                    anchorEl={anchorEl}
                                    keepMounted
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                    className={classes.menu}
                                    style={{
                                        marginLeft: 5,
                                        maxWidth: 320,
                                    }}
                                >
                                    <MenuItem
                                        value={-1}
                                        style={{
                                            width: 300,
                                            display: images.length === 0 ? "block" : "none",
                                        }}
                                    >
                                        None
                                    </MenuItem>
                                    <MenuItem
                                        style={{
                                            display: images.length > 0 ? "block" : "none",
                                        }}
                                    >
                                        <Input
                                            placeholder="Search Raster Images"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                            onChange={searchImagery}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            }
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <FilterList />
                                                </InputAdornment>
                                            }
                                        />
                                    </MenuItem>
                                    <Scrollbars
                                        autoHeight
                                        autoHeightMax={300}
                                        style={{ overflowX: "hidden", width: "100%" }}
                                    >
                                        {images.map((image: any, index: number) => {
                                            return filteredImages.map((filterImage: any, count: number) => {
                                                if (image.name === filterImage.name) {
                                                    return (
                                                        <MenuItem
                                                            className={classes.menuItem}
                                                            key={"img-" + count + "-" + index}
                                                            value={index}
                                                            onClick={addRasterImage}
                                                            title={image.name}
                                                        >
                                                            <ListItemIcon>
                                                                <RasterIcon
                                                                    className={classes.icon}
                                                                    key={"icon-" + count + "-" + index}
                                                                />
                                                            </ListItemIcon>
                                                            <div className={classes.name}>{image.name}</div>
                                                        </MenuItem>
                                                    );
                                                }
                                                return <div key={"div-" + count + "-" + index} />;
                                            });
                                        })}
                                    </Scrollbars>
                                </Menu>
                            </td>
                        </tr>
                    </tbody>
                </table>
                {rasterList.map((rasterId: string, index: number) => {
                    return (
                        <RasterImage
                            key={"raster-" + rasterId + index}
                            ID={rasterId}
                            index={index}
                            updateParent={updateParent}
                            refreshRasters={refreshRasters}
                            folderLevel={0}
                            {...props}
                        />
                    );
                })}
                {needsApply ? (
                    <div className={classes.applyButton}>
                        <Button
                            color="primary"
                            onClick={applySettings}
                            title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                        >
                            Apply
                        </Button>
                    </div>
                ) : (
                    ""
                )}
            </div>
        </Section>
    );
}
